import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@wayflyer/auth-sdk";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import { AxiosError } from "axios";
import { getStageCompletionStatusQueryOptions } from "shared-ui";

const CREDIT_SCORE_CONSENT_API = "/:customerSlug/funding/api/credit-score-consent/";

const useCreditScoreConsentQueryOptions = (customerDomain: string) => {
  const client = useAxios();
  const fetchUrl = CREDIT_SCORE_CONSENT_API.replace(":customerSlug", customerDomain);

  return queryOptions({
    queryKey: [fetchUrl, customerDomain],
    queryFn: () => client.get<FundingApplicationSchemas.CreditScoreConsentResponseSchema>(fetchUrl),
    select: ({ data }) => data,
    retry: (failureCount, error) => {
      // Don't retry on 404 - it just means no consent exists yet
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useCreditScoreConsent = (customerDomain: string) => {
  const options = useCreditScoreConsentQueryOptions(customerDomain);
  return useQuery(options);
};

export const useCreditScoreConsentMutation = (customerDomain: string) => {
  const client = useAxios();
  const queryClient = useQueryClient();
  const mutationUrl = CREDIT_SCORE_CONSENT_API.replace(":customerSlug", customerDomain);

  return useMutation({
    mutationKey: [mutationUrl, customerDomain],
    mutationFn: (data: FundingApplicationSchemas.CreditScoreConsentRequestSchema) => {
      return client.post<FundingApplicationSchemas.CreditScoreConsentResponseSchema>(mutationUrl, data);
    },
    onSuccess: async () => {
      const queryKey = [mutationUrl, customerDomain];
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: getStageCompletionStatusQueryOptions(customerDomain).queryKey });
    },
  });
};

export const useUpdateCreditScoreConsentMutation = (customerDomain: string) => {
  const client = useAxios();
  const queryClient = useQueryClient();
  const mutationUrl = CREDIT_SCORE_CONSENT_API.replace(":customerSlug", customerDomain);

  return useMutation({
    mutationKey: [mutationUrl, customerDomain],
    mutationFn: (data: FundingApplicationSchemas.CreditScoreConsentRequestSchema) => {
      return client.patch<FundingApplicationSchemas.CreditScoreConsentResponseSchema>(mutationUrl, data);
    },
    onSuccess: async () => {
      const queryKey = [mutationUrl, customerDomain];
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: getStageCompletionStatusQueryOptions(customerDomain).queryKey });
    },
  });
};

const useDownloadConsentDocumentQueryOptions = (customerDomain: string) => {
  const client = useAxios();
  const fetchUrl = `${CREDIT_SCORE_CONSENT_API.replace(":customerSlug", customerDomain)}consent-document`;

  return queryOptions({
    queryKey: [fetchUrl, customerDomain],
    queryFn: () => client.get<Blob>(fetchUrl, { responseType: "blob" }),
    select: ({ data }) => data,
    enabled: false,
  });
};

export const useDownloadConsentDocument = (customerDomain: string) => {
  const options = useDownloadConsentDocumentQueryOptions(customerDomain);
  return useQuery(options);
};
